import { Icon, Tooltip } from '@frontend/web-react';
import type { IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, safeCss } from '@frontend/web-utils';
import type { SidebarItemColor } from '@frontend/web/components/Navigation/SidebarItems/types';
import { NavLink as MNavLink } from '@mantine/core';
import type { ReactNode } from 'react';

type SidebarButtonProps = {
  className?: ClassName;
  color?: SidebarItemColor;
  isCollapsed?: boolean;
  label: string;
  leftIconName?: IconName;
  onClick: () => void;
  rightSection?: ReactNode;
};

export const SidebarButton = ({
  className,
  color = 'primary',
  isCollapsed = false,
  label,
  leftIconName,
  onClick,
  rightSection,
}: SidebarButtonProps) => {
  return (
    <Tooltip classNames={{ tooltip: safeCss('text-textInverse') }} disabled={!isCollapsed} label={label} position="right">
      <MNavLink
        aria-label={label}
        classNames={{
          body: isCollapsed ? '' : safeCss('py-xs'),
          label: getLabelClassName({ color }),
          root: concatClasses(
            safeCss('sidebar-hover-background-effect gap-x-xs'),
            isCollapsed
              ? safeCss('size-xxl flex items-center justify-center p-sm rounded-md')
              : safeCss('rounded-md py-sm px-md'),
            className,
          ),
        }}
        component={'button'}
        label={isCollapsed && leftIconName ? <Icon color="secondary" name={leftIconName} /> : label}
        leftSection={isCollapsed ? null : leftIconName ? <Icon color="primary" name={leftIconName} /> : undefined}
        onClick={onClick}
        rightSection={isCollapsed ? undefined : rightSection}
      />
    </Tooltip>
  );
};

const getLabelClassName = ({ color }: PickRequired<SidebarButtonProps, 'color'>) => {
  const labelClassMap = {
    danger: safeCss('text-textError'),
    primary: safeCss('text-textPrimary'),
    secondary: safeCss('text-textSecondary'),
  };

  return concatClasses(safeCss('font-medium text-h6'), labelClassMap[color]);
};
